import {
  Squares2X2Icon, BoltIcon, Battery50Icon,
  DocumentCheckIcon, PuzzlePieceIcon, ClipboardDocumentListIcon, FireIcon, FolderOpenIcon,
  SunIcon, ServerIcon, Battery100Icon, PowerIcon, TagIcon, BookOpenIcon, CubeIcon, ScaleIcon, MegaphoneIcon
} from '@heroicons/react/24/outline'
import IkonaAsystenta from '../components/ikony/IkonaAsystenta'
import IkonaDoboruLadowarki from '../components/ikony/IkonaDoboruLadowarki'
import i18n from '../i18n'
import { jestPolska } from '../kraj'

// Nazwy grup i pozycji w słowniku `wspolne` (narzedzia.*). Gettery, a nie gotowe napisy:
// tekst czytamy przy renderze, gdy słowniki są już wczytane.
const tn = (klucz) => i18n.t(`wspolne:narzedzia.${klucz}`)

/**
 * Nawigacja po narzędziach. Dwa przełączniki, niezależne od siebie.
 *
 * ═══ MENU_NARZEDZI (włączone) ═══
 * Górny pasek: „Zostań partnerem”, dwa główne narzędzia z nazwą („Sprawdź ofertę”, „Asystent
 * doboru”), przycisk „Narzędzia” z panelem wszystkich grup, porównanie, motyw i konto. Na każdej
 * stronie, także głównej. Poniżej `lg` przycisk menu otwiera szufladę z tymi samymi grupami.
 *
 * ═══ PASEK_NARZEDZI (wyłączony) ═══
 * Pasek boczny po lewej (components/nawigacja/PasekNarzedzi). Właściciel zrezygnował z niego
 * 2026-10-08 na rzecz menu w górnym pasku; kod zostaje. Wyłączony nie zmienia układu strony:
 * treść ani kafelek zgłoszeń nie przesuwają się nigdzie (UkladZPaskiem zwraca dawny element).
 *
 * Obie flagi wyłączone = górny pasek dokładnie sprzed zmian (dawne ikony, bez przycisku menu).
 *
 * ═══ JEDNO ŹRÓDŁO ═══
 * Te same grupy rysuje panel „Narzędzia”, szuflada na telefonie i pasek boczny. Nowe narzędzie
 * dopisuje się tylko tutaj.
 */
export const MENU_NARZEDZI = true
export const PASEK_NARZEDZI = false

/**
 * Strony z WYŚRODKOWANĄ treścią (kontener `max-w-* mx-auto`): pasek leży tam w pustym lewym marginesie
 * i nie przesuwa treści, dopóki margines wystarcza (uwaga właściciela 2026-10-07: „nie trzeba go
 * specjalnie mieścić, wchodzi z zapasem”). Pozostałe strony mają treść na całą szerokość (listy
 * z filtrami, strona główna, baza wiedzy, obserwowane, porównanie), więc tam pasek odsuwa treść o swoją
 * szerokość. Nowa strona spoza listy dostaje odsunięcie: bezpieczniej odsunąć niż przykryć filtry.
 * Porównanie po całych segmentach adresu (`czyAktywna` w ListaNarzedzi).
 */
export const TRASY_W_MARGINESIE = [
  '/kompatybilnosc', '/okazje', '/ladowarki', '/ladowarki-firmowe',
  '/dobor-zestawu-pv', '/dobierz-zestaw', '/potwierdzenie-zestawu', '/wybor-wykonawcy',
  '/dobor-falownika', '/dobor-modulow', '/dobor-magazynu', '/dobor-ladowarki',
  '/sprawdz-oferte', '/rynek', '/zgloszenie-mikroinstalacji', '/zawiadomienie-psp', '/teczka-pme2', '/teczka', '/klienci',
  '/panel', '/inwerter', '/magazyn', '/ladowarka', '/model',
  '/konto', '/preferencje', '/regulamin', '/polityka-prywatnosci', '/dodaj-siebie', '/zostan-wykonawca',
]

/**
 * `takze`: inne adresy, na których pozycja też ma świecić jako aktywna.
 * Karta modelu nie jest osobnym narzędziem, a bez tego kto wejdzie z listy
 * modułów w szczegóły, traci w pasku informację, gdzie jest.
 *
 * Ikony w „Dobór” są te same co w dawnym menu asystentów (MenuAsystentow),
 * więc kto je znał, rozpozna je od razu. Wyjątki (2026-10-08): asystent zestawu ma
 * własny dom z panelami zamiast gwiazdek, a dobór ładowarki wtyczkę zamiast
 * procesora (components/ikony). Katalog ma INNE ikony, bo na zwiniętym
 * pasku widać same ikony i dwie identyczne błyskawice jedna pod drugą
 * wyglądałyby na pomyłkę.
 *
 * Nazwy grup to rzeczowniki tej samej formy (decyzja właściciela 2026-10-08):
 * „Sprawdź” nie obejmowało zgłoszenia do operatora; właściciel wybrał „Narzędzia” (2026-10-08).
 *
 * `tylkoPolska`: pozycja znika w wersji krajowej (src/kraj.js), bo działa tylko po polsku albo
 * na polskich regułach: kreatory pojedynczych komponentów, „Sprawdź ofertę”, zgłoszenie do OSD
 * i baza wiedzy. Grupa bez pozycji znika razem z nagłówkiem.
 *
 * `bramka`: klucz z config/bramki.js dla narzędzia, które w całości działa dopiero po zalogowaniu. Niezalogowany
 * widzi przy nazwie kłódkę z podpisem „Po zalogowaniu” (ListaNarzedzi). Stan bierzemy z tego samego haka co
 * bramka na stronie, więc kłódka znika razem z nią.
 *
 * `funkcja`: klucz funkcji planu Premium z config/plany.js (FE-04). Zalogowany bez tej funkcji (Free, koniec próby)
 * widzi przy nazwie plakietkę „Premium”; strona narzędzia pokazuje wtedy kartę z oknem „Przejdź na Premium”.
 */
const WSZYSTKIE_GRUPY = [
  {
    get tytul() { return tn('grupa.dobor') },
    pozycje: [
      // Prosty tryb asystenta (/dobierz-zestaw) to ten sam silnik, a przełącznik
      // między trybami stoi w samym asystencie (kreator/WyborDrogi).
      { to: '/dobor-zestawu-pv', get label() { return tn('pozycja.asystent_zestawu') }, Icon: IkonaAsystenta, takze: ['/dobierz-zestaw'] },
      { to: '/dobor-modulow', get label() { return tn('pozycja.dobor_modulow') }, Icon: Squares2X2Icon, tylkoPolska: true },
      { to: '/dobor-falownika', get label() { return tn('pozycja.dobor_falownika') }, Icon: BoltIcon, tylkoPolska: true },
      { to: '/dobor-magazynu', get label() { return tn('pozycja.dobor_magazynu') }, Icon: Battery50Icon, tylkoPolska: true },
      { to: '/dobor-ladowarki', get label() { return tn('pozycja.dobor_ladowarki') }, Icon: IkonaDoboruLadowarki, tylkoPolska: true },
    ],
  },
  {
    get tytul() { return tn('grupa.narzedzia') },
    pozycje: [
      { to: '/sprawdz-oferte', get label() { return tn('pozycja.sprawdz_oferte') }, Icon: DocumentCheckIcon, tylkoPolska: true },
      // Rynek zleceń (2026-10-21): „Ogłoś na Rynku”, ogłoszenie z kontem (bramka `rynek` na stronie).
      { to: '/rynek', get label() { return tn('pozycja.rynek') }, Icon: MegaphoneIcon, tylkoPolska: true },
      { to: '/porownanie', get label() { return tn('pozycja.porownywarka') }, Icon: ScaleIcon },
      { to: '/kompatybilnosc', get label() { return tn('pozycja.kompatybilnosc') }, Icon: PuzzlePieceIcon },
      { to: '/zgloszenie-mikroinstalacji', get label() { return tn('pozycja.zgloszenie_osd') }, Icon: ClipboardDocumentListIcon, tylkoPolska: true, bramka: 'zgloszenieOsd', funkcja: 'zgloszenie_osd' },
      { to: '/zawiadomienie-psp', get label() { return tn('pozycja.zawiadomienie_psp') }, Icon: FireIcon, tylkoPolska: true, bramka: 'zawiadomieniePsp', funkcja: 'zawiadomienie_psp' },
      { to: '/projekt-3d', get label() { return tn('pozycja.projekt_3d') }, Icon: CubeIcon, tylkoPolska: true, takze: ['/projekt-dachu-3d'], bramka: 'projekt3d', funkcja: 'projekt3d' },
      { to: '/teczka-pme2', get label() { return tn('pozycja.teczka_pme2') }, Icon: FolderOpenIcon, tylkoPolska: true, bramka: 'teczkaPme2', funkcja: 'teczka_pme2' },
    ],
  },
  {
    get tytul() { return tn('grupa.katalog') },
    pozycje: [
      { to: '/panele', get label() { return tn('pozycja.moduly') }, Icon: SunIcon, takze: ['/panel', '/model/panel'] },
      { to: '/inwertery', get label() { return tn('pozycja.falowniki') }, Icon: ServerIcon, takze: ['/inwerter', '/model/inwerter'] },
      { to: '/magazyny', get label() { return tn('pozycja.magazyny') }, Icon: Battery100Icon, takze: ['/magazyn', '/model/magazyn'] },
      // Ładowarek nie zbieramy jeszcze w żadnym kraju poza Polską (brak ofert w schemacie kraju).
      { to: '/ladowarki', get label() { return tn('pozycja.ladowarki') }, Icon: PowerIcon, takze: ['/ladowarka', '/model/wallbox'], tylkoPolska: true },
      { to: '/okazje', get label() { return tn('pozycja.okazje') }, Icon: TagIcon },
    ],
  },
  {
    get tytul() { return tn('grupa.wiedza') },
    pozycje: [
      { to: '/baza-wiedzy', get label() { return tn('pozycja.baza_wiedzy') }, Icon: BookOpenIcon, tylkoPolska: true },
    ],
  },
]

// Filtr w miejscu, bez kopiowania obiektów: kopia (`{ ...g }`) odczytałaby gettery z nazwami
// raz, przy wczytaniu modułu, zanim słowniki będą gotowe.
if (!jestPolska) {
  WSZYSTKIE_GRUPY.forEach((g) => { g.pozycje = g.pozycje.filter((p) => !p.tylkoPolska) })
}
export const GRUPY_NARZEDZI = WSZYSTKIE_GRUPY.filter((g) => g.pozycje.length > 0)
